import React, { useState, useRef, useEffect } from "react";
import { FiChevronDown } from "react-icons/fi";

export interface DropdownOption {
    label: string;
    value: string | number;
}

const ChevronDown = FiChevronDown as any;

interface SelectDropdownProps {
    label?: string;
    name: string;
    value: string | number;
    onChange: (value: string | number) => void;
    options: DropdownOption[];
    placeholder?: string;
    required?: boolean;
    error?: string;
    disabled?: boolean;
    onSearchChange?: (term: string) => void;
    isLoading?: boolean;
}

const DropdownNew: React.FC<SelectDropdownProps> = ({
    label,
    name,
    value,
    onChange,
    options,
    placeholder = "Select",
    required,
    error,
    disabled = false,
    onSearchChange,
    isLoading = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const wrapperRef = useRef<HTMLDivElement>(null);

    const selectedLabel =
        options.find((o) => o.value === value)?.label || placeholder;

    // Use internal filtering only if external search is NOT provided.
    // If onSearchChange is provided, we assume the parent filters 'options'.
    const filteredOptions = onSearchChange
        ? options
        : options.filter((opt) =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase())
        );

    // click outside close
    useEffect(() => {
        const listener = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", listener);
        return () => document.removeEventListener("mousedown", listener);
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const term = e.target.value;
        setSearchTerm(term);
        if (onSearchChange) {
            onSearchChange(term);
        }
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            {/* Dropdown Box */}
            {label && (
                <label
                    style={{
                        display: "block",
                        marginBottom: "8px",
                        color: "#101828",
                        fontSize: "14px",
                        fontWeight: 500,
                    }}
                    className="!text-sm"
                >
                    {label}{required && <span className="text-red-500">*</span>}
                </label>
            )}

            <div
                onClick={() => {
                    if (!disabled) setIsOpen(!isOpen);
                }}
                className={`
                    w-full h-[48px]
                    bg-[#F3F3F5]
                    !rounded-[8px]
                    px-3 pr-9
                    flex items-center
                    text-gray-500
                    cursor-pointer
                     ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
                `}
                style={{
                    border: error ? "1px solid #EF4444" : "none",
                    fontSize: "14px",
                }}
            >
                {!isOpen && (
                    <span className="text-gray-600 line-clamp-1">
                        {selectedLabel}
                    </span>
                )}
                {isOpen && (
                    <input
                        autoFocus
                        value={searchTerm}
                        onChange={handleSearchChange}
                        placeholder="Search here..."
                        style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            outline: "none",
                            boxShadow: "none",
                            background: "transparent",
                            fontSize: "14px",
                            WebkitTapHighlightColor: "transparent",
                        }}
                        onClick={(e) => e.stopPropagation()} // prevent close
                    />
                )}

                <ChevronDown
                    size={18}
                    className={`absolute right-3 text-gray-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""
                        }`}
                />
            </div>

            {/* Options */}
            {isOpen && (
                <ul
                    className="
            absolute z-20 
            bg-white
            mt-1 rounded-md shadow-md 
            border border-gray-200
            max-h-48 w-full overflow-y-auto
            !p-0
        "
                >
                    {isLoading ? (
                        <li className="px-3 py-2 text-sm text-gray-500 flex items-center justify-center gap-2">
                            <svg className="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Loading tasks...</span>
                        </li>
                    ) : filteredOptions.length === 0 ? (
                        <li className="px-3 py-2 text-sm text-gray-400">No results found</li>
                    ) : (
                        filteredOptions.map((opt) => (
                            <li
                                key={opt.value}
                                onClick={() => {
                                    if (disabled) return;
                                    onChange(opt.value);
                                    setIsOpen(false);
                                    setSearchTerm(""); // reset internal search
                                    if (onSearchChange) onSearchChange(""); // reset external search
                                }}
                                className={`
                                    px-3 py-2 text-sm
                                    ${disabled ? "cursor-not-allowed text-gray-400" : "cursor-pointer hover:bg-gray-100"}
                                `}
                            >
                                {opt.label}
                            </li>
                        ))
                    )}
                </ul>
            )}

            {/* Error message */}
            {error && (
                <p
                    style={{
                        color: "#EF4444",
                        fontSize: "13px",
                        marginTop: "4px",
                    }}
                >
                    {error}
                </p>
            )}
        </div>
    );
};

export default DropdownNew;